import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SearchComponent from '../../components/ui/SearchComponent';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConversationList from './components/ConversationList';
import MessagePanel from './components/MessagePanel';
import NotificationCenter from './components/NotificationCenter';
import ContactsDirectory from './components/ContactsDirectory';
import VideoCallPanel from './components/VideoCallPanel';

const CommunicationHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('messages');
  const [activeConversation, setActiveConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isVideoCallActive, setIsVideoCallActive] = useState(false);
  const [showMobileMessageView, setShowMobileMessageView] = useState(false);
  const [currentUser] = useState({
    id: 1,
    name: 'John Doe',
    role: 'student',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
  });

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/student-dashboard' },
    { label: 'Communication Hub' }
  ];

  const tabOptions = [
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'contacts', label: 'Contacts', icon: 'Users' }
  ];

  // Mock conversations data
  const [conversations] = useState([
    {
      id: 1,
      name: 'Smart Traffic Team',
      type: 'team',
      avatar: null,
      lastMessage: 'Great progress on the prototype! Let\'s schedule a review meeting.',
      lastMessageTime: new Date(Date.now() - 300000),
      unreadCount: 3,
      isOnline: true,
      isPinned: true,
      isMuted: false,
      memberCount: 5
    },
    {
      id: 2,
      name: 'Dr. Sarah Johnson',
      type: 'mentor',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      lastMessage: 'I\'ve reviewed your project proposal. Can we discuss the technical approach?',
      lastMessageTime: new Date(Date.now() - 1800000),
      unreadCount: 1,
      isOnline: true,
      isPinned: false,
      isMuted: false
    },
    {
      id: 3,
      name: 'City of Austin - Transportation Dept',
      type: 'city',
      avatar: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=150&h=150&fit=crop&crop=center',
      lastMessage: 'Thank you for submitting the initial requirements. We have some feedback to share.',
      lastMessageTime: new Date(Date.now() - 3600000),
      unreadCount: 0,
      isOnline: false,
      isPinned: false,
      isMuted: false
    },
    {
      id: 4,
      name: 'Alex Chen',
      type: 'direct',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      lastMessage: 'Hey! Are you available for the team standup at 3 PM?',
      lastMessageTime: new Date(Date.now() - 7200000),
      unreadCount: 0,
      isOnline: true,
      isPinned: false,
      isMuted: false
    },
    {
      id: 5,
      name: 'Urban Planning Cohort',
      type: 'team',
      avatar: null,
      lastMessage: 'Don\'t forget about tomorrow\'s workshop on GIS mapping techniques.',
      lastMessageTime: new Date(Date.now() - 14400000),
      unreadCount: 2,
      isOnline: false,
      isPinned: false,
      isMuted: true,
      memberCount: 12
    }
  ]);

  // Mock messages data
  const [messages, setMessages] = useState([
    {
      id: 1,
      content: 'Hi everyone! I\'ve completed the initial research on traffic flow patterns in downtown Austin.',
      sender: {
        id: 2,
        name: 'Alex Chen',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      },
      timestamp: new Date(Date.now() - 3600000),
      type: 'text',
      status: 'read'
    },
    {
      id: 2,
      content: 'That\'s excellent work, Alex! Could you share the data visualization you created?',
      sender: currentUser,
      timestamp: new Date(Date.now() - 3300000),
      type: 'text',
      status: 'read'
    },
    {
      id: 3,
      content: 'Absolutely! Here\'s the traffic analysis report with interactive charts.',
      sender: {
        id: 2,
        name: 'Alex Chen',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      },
      timestamp: new Date(Date.now() - 3000000),
      type: 'file',
      fileName: 'traffic_analysis_report.pdf',
      status: 'read'
    },
    {
      id: 4,
      content: 'Great progress on the prototype! Let\'s schedule a review meeting.',
      sender: {
        id: 3,
        name: 'Maria Rodriguez',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
      },
      timestamp: new Date(Date.now() - 300000),
      type: 'text',
      status: 'delivered',
      reactions: [
        { emoji: '👍', count: 2 },
        { emoji: '🎉', count: 1 }
      ]
    }
  ]);

  // Mock notifications data
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'New Team Invitation',
      message: 'You\'ve been invited to join the Smart Parking Solutions team for the Austin Smart City Challenge.',
      type: 'team',
      priority: 'high',
      timestamp: new Date(Date.now() - 900000),
      isRead: false,
      actionRequired: true,
      primaryAction: 'Accept',
      secondaryAction: 'Decline',
      metadata: {
        sender: 'Dr. Sarah Johnson',
        project: 'Smart Parking Solutions'
      }
    },
    {
      id: 2,
      title: 'Milestone Submission Approved',
      message: 'Your Problem Brief submission for the Traffic Optimization project has been approved by the city review committee.',
      type: 'milestone',
      priority: 'medium',
      timestamp: new Date(Date.now() - 3600000),
      isRead: false,
      actionRequired: true,
      primaryAction: 'View Feedback',
      metadata: {
        project: 'Traffic Optimization',
        deadline: new Date(Date.now() + 604800000)
      }
    },
    {
      id: 3,
      title: 'Upcoming Workshop Reminder',
      message: 'IoT Sensors and Data Collection workshop starts in 2 hours. Join the virtual session to learn about smart city sensor networks.',
      type: 'meeting',
      priority: 'medium',
      timestamp: new Date(Date.now() - 7200000),
      isRead: true,
      actionRequired: true,
      primaryAction: 'Join Workshop',
      metadata: {
        deadline: new Date(Date.now() + 7200000)
      }
    },
    {
      id: 4,
      title: 'System Maintenance Notice',
      message: 'The platform will undergo scheduled maintenance tonight from 11 PM to 2 AM EST. Some features may be temporarily unavailable.',
      type: 'system',
      priority: 'low',
      timestamp: new Date(Date.now() - 14400000),
      isRead: true,
      actionRequired: false
    },
    {
      id: 5,
      title: 'New Message from City Official',
      message: 'Austin Transportation Department has shared additional requirements for your traffic management solution.',
      type: 'message',
      priority: 'high',
      timestamp: new Date(Date.now() - 21600000),
      isRead: true,
      actionRequired: true,
      primaryAction: 'View Message',
      metadata: {
        sender: 'Austin Transportation Dept'
      }
    }
  ]);

  // Mock contacts data
  const [contacts] = useState([
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      email: 'sarah.johnson@university.edu',
      role: 'mentor',
      organization: 'University of Texas',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      status: 'online',
      lastSeen: null
    },
    {
      id: 2,
      name: 'Alex Chen',
      email: 'alex.chen@student.edu',
      role: 'student',
      organization: 'UT Austin',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      status: 'online',
      lastSeen: null
    },
    {
      id: 3,
      name: 'Maria Rodriguez',
      email: 'maria.rodriguez@student.edu',
      role: 'student',
      organization: 'UT Austin',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      status: 'away',
      lastSeen: null
    },
    {
      id: 4,
      name: 'James Wilson',
      email: 'james.wilson@austin.gov',
      role: 'city-official',
      organization: 'City of Austin',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      status: 'busy',
      lastSeen: null
    },
    {
      id: 5,
      name: 'Emily Davis',
      email: 'emily.davis@pcf.org',
      role: 'pcf-admin',
      organization: 'Pathway Community Foundation',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
      status: 'offline',
      lastSeen: '2 hours ago'
    },
    {
      id: 6,
      name: 'David Kim',
      email: 'david.kim@student.edu',
      role: 'student',
      organization: 'Prairie View A&M',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      status: 'online',
      lastSeen: null
    }
  ]);

  // Mock video call participants
  const [videoCallParticipants] = useState([
    {
      id: 1,
      name: 'John Doe',
      role: 'Student',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      isMuted: false,
      videoEnabled: true
    },
    {
      id: 2,
      name: 'Alex Chen',
      role: 'Student',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      isMuted: true,
      videoEnabled: true
    },
    {
      id: 3,
      name: 'Dr. Sarah Johnson',
      role: 'Mentor',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      isMuted: false,
      videoEnabled: false
    }
  ]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
    setShowMobileMessageView(true);
  };

  const handleBackToList = () => {
    setShowMobileMessageView(false);
    setActiveConversation(null);
  };

  const handleSendMessage = (message) => {
    setMessages(prev => [...prev, message]);
  };

  const handleFileUpload = (file) => {
    const fileMessage = {
      id: Date.now(),
      content: `Shared a file: ${file?.name}`,
      sender: currentUser,
      timestamp: new Date(),
      type: 'file',
      fileName: file?.name
    };
    setMessages(prev => [...prev, fileMessage]);
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => 
      prev?.map(notification => 
        notification?.id === notificationId 
          ? { ...notification, isRead: true }
          : notification
      )
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => 
      prev?.map(notification => ({ ...notification, isRead: true }))
    );
  };

  const handleNotificationAction = (notificationId, actionType) => {
    console.log(`Notification ${notificationId} action: ${actionType}`);
    // Handle notification actions here
  };

  const handleStartConversation = (contact) => {
    // Create or find existing conversation with contact
    const existingConversation = conversations?.find(conv => 
      conv?.type === 'direct' && conv?.name === contact?.name
    );
    
    if (existingConversation) {
      setActiveConversation(existingConversation);
    } else {
      const newConversation = {
        id: Date.now(),
        name: contact?.name,
        type: 'direct',
        avatar: contact?.avatar,
        lastMessage: 'Start a conversation...',
        lastMessageTime: new Date(),
        unreadCount: 0,
        isOnline: contact?.status === 'online',
        isPinned: false,
        isMuted: false
      };
      setActiveConversation(newConversation);
    }
    setActiveTab('messages');
  };

  const handleCallContact = (contact) => {
    setIsVideoCallActive(true);
  };

  const handleEndCall = () => {
    setIsVideoCallActive(false);
  };

  const handleToggleMute = (muted) => {
    console.log('Mute toggled:', muted);
  };

  const handleToggleVideo = (videoOff) => {
    console.log('Video toggled:', videoOff);
  };

  const handleToggleScreenShare = (sharing) => {
    console.log('Screen share toggled:', sharing);
  };

  const handleSearch = (query, scope) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const unreadNotifications = notifications?.filter(n => !n?.isRead)?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole={currentUser?.role}
        userName={currentUser?.name}
        notifications={unreadNotifications}
      />
      <main className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Communication Hub</h1>
              <p className="text-muted-foreground">
                Connect with your team, mentors, and city officials in real-time
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <SearchComponent
                placeholder="Search conversations..."
                onSearch={handleSearch}
                onClear={handleClearSearch}
                searchScope="all"
                expandable={true}
              />
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
              >
                New Chat
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
          {/* Sidebar - Hidden on mobile when message is open */}
          <div className={`lg:col-span-1 bg-card border border-border rounded-lg shadow-soft overflow-hidden ${
            showMobileMessageView ? 'hidden lg:block' : 'block'
          }`}>
            {/* Tab Navigation */}
            <div className="flex border-b border-border">
              {tabOptions?.map((tab) => (
                <Button
                  key={tab?.id}
                  variant={activeTab === tab?.id ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab(tab?.id)}
                  iconName={tab?.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="flex-1 rounded-none border-r border-border last:border-r-0"
                >
                  {tab?.label}
                  {tab?.id === 'notifications' && unreadNotifications > 0 && (
                    <span className="ml-2 bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                      {unreadNotifications}
                    </span>
                  )}
                </Button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="h-full">
              {activeTab === 'messages' && (
                <ConversationList
                  conversations={conversations}
                  activeConversation={activeConversation}
                  onSelectConversation={handleSelectConversation}
                  searchQuery={searchQuery}
                />
              )}
              
              {activeTab === 'notifications' && (
                <NotificationCenter
                  notifications={notifications}
                  onMarkAsRead={handleMarkAsRead}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onNotificationAction={handleNotificationAction}
                />
              )}
              
              {activeTab === 'contacts' && (
                <ContactsDirectory
                  contacts={contacts}
                  onStartConversation={handleStartConversation}
                  onCallContact={handleCallContact}
                />
              )}
            </div>
          </div>

          {/* Main Content - Full width on mobile when message is open */}
          <div className={`bg-card border border-border rounded-lg shadow-soft overflow-hidden ${
            showMobileMessageView ? 'col-span-1 lg:col-span-3' : 'hidden lg:block lg:col-span-3'
          }`}>
            {/* Mobile Back Button */}
            {showMobileMessageView && activeTab === 'messages' && (
              <div className="lg:hidden border-b border-border p-3 bg-card">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToList}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  iconSize={16}
                  className="text-foreground hover:bg-accent"
                >
                  Back to Messages
                </Button>
              </div>
            )}
            
            {activeTab === 'messages' ? (
              <MessagePanel
                conversation={activeConversation}
                messages={messages}
                onSendMessage={handleSendMessage}
                onFileUpload={handleFileUpload}
                currentUser={currentUser}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon 
                    name={activeTab === 'notifications' ? 'Bell' : 'Users'} 
                    size={64} 
                    className="text-muted-foreground mx-auto mb-4" 
                  />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {activeTab === 'notifications' ? 'Notification Center' : 'Contacts Directory'}
                  </h3>
                  <p className="text-muted-foreground">
                    {activeTab === 'notifications' 
                      ? 'Manage your notifications from the sidebar' :'Browse and connect with your contacts from the sidebar'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Video Call Panel */}
      <VideoCallPanel
        isActive={isVideoCallActive}
        participants={videoCallParticipants}
        onEndCall={handleEndCall}
        onToggleMute={handleToggleMute}
        onToggleVideo={handleToggleVideo}
        onToggleScreenShare={handleToggleScreenShare}
      />
    </div>
  );
};

export default CommunicationHub;